import { ReactNode, useEffect, useRef } from "react";

import { markAhead, prefersReducedMotion } from "./Phone";

// A feature section whose phone plays its conversation as you scroll.
//
// On a large screen the whole section (copy and phone) pins under the header
// while the chat scrolls inside the phone, then lets go. Where the copy
// wouldn't fit beside a pinned phone (phones, short laptops) only the phone
// sticks, below the copy. `stepAt` lights up the copy's numbered steps as the
// chat reaches them (fractions of the conversation, one per step).
//
// Prerendered as a normal section with a scrollable chat; all of this is
// added after load, and skipped for reduced motion.

const HEADER_H = 69;
const WIDE = "(min-width: 1024px) and (min-height: 700px)";

export function ScrollStage({
  id,
  labelledBy,
  flip,
  stepAt,
  copy,
  phone,
}: {
  id: string;
  labelledBy: string;
  flip?: boolean;
  stepAt?: number[];
  copy: ReactNode;
  phone: ReactNode;
}) {
  const stageRef = useRef<HTMLElement>(null);
  const stepKey = stepAt?.join(",");

  useEffect(() => {
    const stage = stageRef.current;
    const pin = stage?.querySelector<HTMLElement>(".stage__pin");
    const copyEl = stage?.querySelector<HTMLElement>(".showcase__copy");
    const column = stage?.querySelector<HTMLElement>(".showcase__phone");
    const phoneEl = stage?.querySelector<HTMLElement>(".phone");
    const win = stage?.querySelector<HTMLElement>(".chat-window");
    const track = stage?.querySelector<HTMLElement>(".chat");
    if (
      !stage ||
      !pin ||
      !copyEl ||
      !column ||
      !phoneEl ||
      !win ||
      !track ||
      prefersReducedMotion()
    )
      return;

    const steps = stepKey ? stepKey.split(",").map(Number) : [];
    const wide = window.matchMedia(WIDE);
    const items = Array.from(track.children) as HTMLElement[];
    let tops: number[] = [];
    let overflow = 0;
    let pinned = false;
    let current = 0;
    let raf = 0;
    let visible = false;

    const layout = () => {
      pinned =
        wide.matches &&
        copyEl.offsetHeight + 56 <= window.innerHeight - HEADER_H;
      stage.classList.add("is-live");
      win.classList.add("is-live");
      stage.classList.toggle("is-pinned", pinned);
      stage.classList.toggle("is-tracked", !pinned);
      tops = items.map((el) => el.offsetTop);
      overflow = Math.max(0, track.offsetHeight - win.clientHeight);
      // About 1.7 px of page scroll per px of chat, plus a pause at each end.
      let travel = overflow * 1.7 + (pinned ? 360 : 220);
      // Tracked beside the copy (not wrapped below it): the phone sticks and
      // plays its chat; the copy scrolls on until its end meets the phone's
      // bottom edge, then (if the chat isn't done) holds there too, and the
      // two move on together, like the hero. So the phone's column is at
      // least as tall as the copy, and the copy sticks bottom-aligned with
      // the phone (top-aligned if it's the shorter of the two).
      // Side by side when they don't overlap horizontally (sticking moves
      // the copy vertically, so its top can't tell us).
      const beside =
        !pinned &&
        (column.offsetLeft >= copyEl.offsetLeft + copyEl.offsetWidth ||
          column.offsetLeft + column.offsetWidth <= copyEl.offsetLeft);
      stage.classList.toggle("is-beside", beside);
      if (beside) {
        const phoneH = phoneEl.offsetHeight;
        const stickTop = parseFloat(getComputedStyle(phoneEl).top) || 0;
        travel = Math.max(travel, copyEl.offsetHeight - phoneH);
        stage.style.setProperty(
          "--copy-top",
          `${Math.round(Math.min(stickTop, stickTop + phoneH - copyEl.offsetHeight))}px`,
        );
      }
      stage.style.setProperty("--travel", `${Math.round(travel)}px`);
    };

    const progress = () => {
      const [sticky, box] = pinned ? [pin, stage] : [phoneEl, column];
      const range = box.offsetHeight - sticky.offsetHeight;
      if (range <= 0) return 0;
      const moved =
        sticky.getBoundingClientRect().top - box.getBoundingClientRect().top;
      return Math.min(1, Math.max(0, moved / range));
    };

    const frame = () => {
      const p = progress();
      const chat = Math.min(1, Math.max(0, (p - 0.08) / 0.84));
      const target = chat * overflow;
      current += (target - current) * 0.18;
      if (Math.abs(target - current) < 0.4) current = target;
      track.style.transform = `translate3d(0, ${-current}px, 0)`;
      markAhead(items, tops, current, win.clientHeight);
      stage.style.setProperty("--p", p.toFixed(3));
      if (steps.length) {
        let step = 1;
        steps.forEach((at, i) => {
          if (chat >= at) step = i + 1;
        });
        if (stage.dataset.step !== String(step))
          stage.dataset.step = String(step);
      }
      raf = visible ? requestAnimationFrame(frame) : 0;
    };

    layout();
    const observer = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible && !raf) raf = requestAnimationFrame(frame);
      },
      { rootMargin: "200px 0px" },
    );
    observer.observe(stage);
    const resized = new ResizeObserver(layout);
    resized.observe(track);
    resized.observe(copyEl);
    window.addEventListener("resize", layout);
    wide.addEventListener("change", layout);

    return () => {
      observer.disconnect();
      resized.disconnect();
      window.removeEventListener("resize", layout);
      wide.removeEventListener("change", layout);
      cancelAnimationFrame(raf);
      stage.classList.remove("is-live", "is-pinned", "is-tracked", "is-beside");
      win.classList.remove("is-live");
      stage.style.removeProperty("--travel");
      stage.style.removeProperty("--copy-top");
      stage.style.removeProperty("--p");
      delete stage.dataset.step;
      track.style.transform = "";
      items.forEach((el) => el.classList.remove("is-ahead"));
    };
  }, [stepKey]);

  return (
    <section
      id={id}
      ref={stageRef}
      className="stage"
      aria-labelledby={labelledBy}
    >
      <div className="stage__pin">
        <div className="container stage__inner">
          <div className={flip ? "showcase showcase--flip" : "showcase"}>
            <div className="showcase__copy">{copy}</div>
            <div className="showcase__phone">{phone}</div>
          </div>
        </div>
        <span className="stage__hint" aria-hidden="true">
          Scroll to play the chat
        </span>
      </div>
    </section>
  );
}
